import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV, CONTACT } from "@/lib/content";
import { EASE } from "@/components/anim";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" aria-label="Aeris India Technology home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => {
              const active = pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={`relative font-hud text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    active ? "text-kinetic" : "text-[#9CA3AF] hover:text-white"
                  }`}
                >
                  {item.label}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 right-0 h-px bg-kinetic"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="navbar-cta-quote-button"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-kinetic px-5 py-2.5 font-hud text-[11px] font-semibold uppercase tracking-[0.18em] text-void transition-all duration-300 hover:shadow-[0_0_32px_rgba(255,122,26,0.45)] hover:-translate-y-0.5"
            >
              Request DAAS Quote
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button
              data-testid="navbar-mobile-menu-button"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
              aria-label={open ? "Close menu" : "Open menu"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-40 bg-void/98 hud-grid lg:hidden"
          >
            <div className="flex h-full flex-col justify-between px-6 pt-28 pb-10">
              <nav className="flex flex-col gap-2">
                {[...NAV, { label: "Contact", path: "/contact" }].map((item, i) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                  >
                    <Link
                      to={item.path}
                      data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                      className={`block py-2 font-display text-4xl font-bold tracking-tight ${
                        pathname === item.path ? "text-kinetic" : "text-white"
                      }`}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="font-hud text-xs tracking-[0.2em] text-[#9CA3AF] space-y-2"
              >
                <p data-testid="nav-mobile-email">{CONTACT.email}</p>
                <p data-testid="nav-mobile-phone">{CONTACT.phone}</p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
